import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import artstoriesBackground from '../assets/artstories.png';
import artistwall from '../assets/artistwall.png';

export default function SelectArtworkForStory() {
  const [artworks, setArtworks] = useState([]);
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scrollIndex, setScrollIndex] = useState(0);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
      } else {
        navigate('/login', { state: { from: '/select-artwork-for-story' } });
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    async function fetchArtworks() {
      if (!currentUser) return;
      setLoading(true);
      setError(null);
      try {
        // Fetch user's artworks from the new endpoint
        const res = await fetch(`http://localhost:3001/api/artist-wall/artworks?userId=${currentUser.uid}`);
        if (!res.ok) throw new Error('Failed to fetch artworks');
        const data = await res.json();
        setArtworks(data.artworks || []);
      } catch (err) {
        setError('Could not load your artworks.');
      } finally {
        setLoading(false);
      }
    }
    fetchArtworks();
  }, [currentUser]);

  const handleSelect = (artwork) => {
    setSelectedArtwork(artwork);
  };

  const handleContinue = () => {
    if (selectedArtwork) {
      // Navigate to Tell Artmosphere About Your Artwork page (assume /story-creator)
      navigate('/story-creator', { state: { originalArtwork: selectedArtwork.imageUrl, title: selectedArtwork.title } });
    } else if (filteredArtworks.length === 0) {
      // If no artworks found, navigate to upload artwork page
      navigate('/artist-wall');
    }
  };

  // Filtered artworks based on search
  const filteredArtworks = artworks.filter(a =>
    a.title && a.title.toLowerCase().includes(search.toLowerCase())
  );
  const visibleCount = 4; // Show more artworks at once
  const canScrollLeft = scrollIndex > 0;
  const canScrollRight = filteredArtworks.length > visibleCount && scrollIndex < filteredArtworks.length - visibleCount;
  const visibleArtworks = filteredArtworks.slice(scrollIndex, scrollIndex + visibleCount);

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(${artstoriesBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      />
      {/* Overlay for better text readability */}
      <div className="absolute inset-0 bg-black/20" />
      {/* Back to My Art Stories button at top-left */}
      <button
        className="absolute top-6 left-6 z-20 bg-amber-400 hover:bg-amber-500 text-white font-bold py-2 px-6 rounded-xl shadow-lg text-lg border-4 border-amber-200 transition-all duration-300 drop-shadow-xl"
        onClick={() => navigate('/storyboarding-landing')}
        style={{ fontFamily: 'inherit' }}
      >
        &#8592; Back
      </button>
      <div className="relative z-10 flex flex-col items-center py-12 px-2 w-full">
        <div className="max-w-3xl w-full mx-auto mb-10 text-center">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4 drop-shadow-lg" style={{ fontFamily: '"Baloo 2", "Comic Sans MS", cursive' }}>
            Select an Artwork to Start Your Story
          </h1>
          <p className="text-lg sm:text-xl text-white mb-8 drop-shadow-md" style={{ fontFamily: '"Baloo 2", "Comic Sans MS", cursive' }}>
            Choose from your existing artworks below.
          </p>
        </div>
        <div className="w-full flex flex-col items-center">
          {/* Search box */}
          <div className="mb-6 w-full max-w-2xl flex justify-center">
            <input
              type="text"
              value={search}
              onChange={e => { setSearch(e.target.value); setScrollIndex(0); }}
              placeholder="Search artworks by title..."
              className="w-full max-w-xl px-4 py-3 rounded-xl border-2 border-amber-300 focus:border-amber-500 focus:outline-none text-lg shadow-md"
              style={{ fontFamily: 'inherit' }}
            />
          </div>
          {loading && <div className="text-white text-center mb-8">Loading your artworks...</div>}
          {error && <div className="text-red-200 text-center mb-8">{error}</div>}
          {!loading && filteredArtworks.length === 0 && (
            <div className="flex flex-col items-center">
              <img src={artistwall} alt="No artworks" className="w-40 h-40 object-contain mb-4 opacity-60" />
              <div className="text-white text-lg mb-4">No artworks found in your Artist Wall.</div>
              <div className="text-white/80 text-sm mb-6 text-center max-w-md">
                Upload an artwork to start creating your story!
              </div>
            </div>
          )}
          {/* Large preview of selected artwork */}
          {selectedArtwork && (
            <div className="mb-8 flex flex-col items-center">
              <img
                src={selectedArtwork.imageUrl}
                alt={selectedArtwork.title || 'Selected Artwork'}
                className="w-[28rem] h-[28rem] object-cover rounded-2xl border-4 border-amber-400 shadow-xl bg-amber-50 mb-2"
              />
              <div className="text-amber-800 font-bold text-lg text-center truncate w-full px-2">{selectedArtwork.title || 'Untitled'}</div>
            </div>
          )}
          {/* Horizontal scrollable row of thumbnails */}
          {filteredArtworks.length > 0 && (
            <div className="flex flex-row items-center gap-4 mb-12 w-full max-w-5xl justify-center">
              {canScrollLeft && (
                <button
                  className="bg-amber-300 hover:bg-amber-400 text-white rounded-full w-12 h-12 flex items-center justify-center shadow-lg text-3xl"
                  onClick={() => setScrollIndex(scrollIndex - 1)}
                  aria-label="Scroll left"
                >
                  &#8592;
                </button>
              )}
              <div className="flex flex-row gap-6 overflow-x-auto scrollbar-hide py-2">
                {visibleArtworks.map((artwork, idx) => (
                  <div
                    key={artwork.imageUrl + idx}
                    className={`relative bg-white/80 border-4 rounded-2xl shadow-xl p-2 flex flex-col items-center cursor-pointer transition-all duration-200 hover:scale-105 ${selectedArtwork && selectedArtwork.imageUrl === artwork.imageUrl ? 'border-amber-400' : 'border-amber-200'}`}
                    onClick={() => handleSelect(artwork)}
                    tabIndex={0}
                    role="button"
                    aria-label={artwork.title || 'Artwork'}
                  >
                    <img
                      src={artwork.imageUrl}
                      alt={artwork.title || 'Artwork'}
                      className="w-40 h-40 object-cover rounded-xl border-2 border-amber-100 bg-amber-50 mb-2"
                    />
                    <div className="text-amber-800 font-semibold text-base text-center truncate w-full px-1">{artwork.title || 'Untitled'}</div>
                  </div>
                ))}
              </div>
              {canScrollRight && (
                <button
                  className="bg-amber-300 hover:bg-amber-400 text-white rounded-full w-12 h-12 flex items-center justify-center shadow-lg text-3xl"
                  onClick={() => setScrollIndex(scrollIndex + 1)}
                  aria-label="Scroll right"
                >
                  &#8594;
                </button>
              )}
            </div>
          )}
        </div>
        <div className="mt-8 flex flex-col items-center gap-4">
          <button
            className={`bg-amber-400 hover:bg-amber-500 text-white font-bold py-3 px-8 rounded-xl shadow-lg text-lg transition-all duration-300 drop-shadow-xl border-4 border-amber-200 ${selectedArtwork || filteredArtworks.length === 0 ? '' : 'opacity-50 cursor-not-allowed'}`}
            onClick={handleContinue}
            disabled={!selectedArtwork && filteredArtworks.length > 0}
          >
            {selectedArtwork ? 'Start your story now' : filteredArtworks.length === 0 ? 'Upload Artwork to Start Story' : 'Select an artwork first'}
          </button>
        </div>
      </div>
    </div>
  );
} 